import React, { FC, useCallback, useEffect, useState } from 'react';

import ColorWrapper from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { IMAGE_HANDLING_CONTEXT, KEYS_TAGS } from '@components/imageHandling/common';
import { SearchItemType } from '@interfaces/base';
import SimpleSearchLineWrapper, { SimpleSearchLineWrapperProps } from '@components/search/common/SimpleSearchLineWrapper';
import { ImageParams, User } from '@interfaces/search';
import { backendCall } from '@reducers/utils';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { ODataDocument } from '@v2/components/imageHandling/types/types';

interface SearchPersonsProps
  extends Pick<SimpleSearchLineWrapperProps<User>, 'C' | 'isPopupMode' | 'popupWidth' | 'loaded' | 'attachMode' | 'scale' | 'font' | 'width'> {
  searchResult: User[];
  onItemSelect: (item: User, itemType: SearchItemType) => void;
}

const SearchPersons: FC<SearchPersonsProps> = ({ isPopupMode, onItemSelect, scale = initialScale, font = initialFont, searchResult, ...rest }) => {
  const dispatch = useAppDispatch();
  const [userImages, setUserImages] = useState<Record<string, ODataDocument | undefined>>({});

  const onItemPress = (user: User) => {
    if (isPopupMode && onItemSelect) {
      onItemSelect(user, 'UserAccount');
      return;
    }
  };

  const getUserImage = useCallback(
    async (userId: string) => {
      const call = {
        method: (api: any) => api.imageHandling.searchDocuments,
        params: {
          search: '*',
          filters: {
            UserAccount: {
              value: userId,
              valueType: 'string',
            },
          },
        },
      };

      return dispatch(backendCall(call));
    },
    [dispatch]
  );

  useEffect(() => {
    const fetchUserImages = async () => {
      const imagePromises = searchResult.map(async (user) => {
        if (user.userProfile?.userId) {
          try {
            const imageResponse = await getUserImage(user.userProfile.userId);
            return { userId: user.userProfile.userId, imageData: imageResponse?.searchResult?.[0] };
          } catch (error) {
            return { userId: user.userProfile.userId, imageData: null };
          }
        }
        return null;
      });

      const results = await Promise.all(imagePromises);
      const imageMap = results.reduce((acc, result) => {
        if (result) {
          acc[result.userId] = result.imageData;
        }
        return acc;
      }, {} as Record<string, ODataDocument>);

      setUserImages(imageMap);
    };

    if (searchResult.length > 0) {
      fetchUserImages();
    }
  }, [searchResult, getUserImage]);

  const getImageParams = (user: User): ImageParams => {
    const userId = user.userProfile?.userId;
    const userImageData = userId ? userImages[userId] : null;

    return {
      documentId: userImageData?.documentId || undefined,
      imageHash: userImageData?.contentHash || undefined,
      imageData: {
        extensionDerivative: userImageData?.extensionDerivative,
        isCropped: userImageData?.isCropped,
        pageNumber: userImageData?.pageNumber,
        rotationAngle: userImageData?.pageDetails?.[userImageData?.pageNumber]?.rotationAngle,
        rotationAngleCrop: userImageData?.cropSettings?.[userImageData?.pageNumber]?.rotationAngle,
      },
      width: scale(66),
      height: scale(44),
      contextId: KEYS_TAGS.UserAccount,
      contextCode: IMAGE_HANDLING_CONTEXT.User,
    };
  };

  return (
    <SimpleSearchLineWrapper
      searchResult={searchResult}
      keyExtractor={(user) => user.userProfile?.userId}
      getImageParams={getImageParams}
      isPopupMode={isPopupMode}
      onItemSelect={(user) => onItemPress(user)}
      getText={(user) => user.userProfile ? `${user.userProfile?.firstName} ${user.userProfile?.lastName}` : ''}
      scale={scale}
      font={font}
      {...rest}
    />
  );
};

export default ColorWrapper(SearchPersons);
